/**
 * PreviewContentPage — Wrapper for content page previews (Header + Content + Footer)
 */
import { computePreviewTokens } from '@/utils/preview-tokens';
import { PreviewHeader } from './PreviewHeader';
import { PreviewFooter } from './PreviewFooter';
import { PreviewCookie } from './PreviewCookie';
import { PreviewAngebot } from './PreviewAngebot';
import { PreviewMethoden } from './PreviewMethoden';
import { PreviewUeberMich } from './PreviewUeberMich';
import { PreviewImpressum } from './PreviewImpressum';
import { PreviewDatenschutz } from './PreviewDatenschutz';
import { PreviewAgb } from './PreviewAgb';

interface PreviewContentPageProps {
  page: 'angebot' | 'methoden' | 'ueberMich' | 'impressum' | 'datenschutz' | 'agb';
  settings: any;
  components: Record<string, any>;
  order: string[];
  onToggleDarkMode: () => void;
}

export function PreviewContentPage({ page, settings, components, order, onToggleDarkMode }: PreviewContentPageProps) {
  const rootStyle = computePreviewTokens(settings);

  const intensity = settings.fxIntensity || "medium";
  const rootClasses = [
    "preview-root",
    settings.darkMode ? "dark" : "",
    settings.fontStack !== "system" ? `font-${settings.fontStack}` : "",
    settings.fxOrbs ? "fx-orbs" : "",
    settings.fxOrbs ? `fx-orbs-${intensity}` : "",
    settings.fxGrain ? "fx-grain" : "",
    settings.fxGrain ? `fx-grain-${intensity}` : "",
    settings.fxGlass ? "fx-glass-cards" : "",
  ].filter(Boolean).join(" ");

  const ContentRenderer = page === 'angebot' ? PreviewAngebot
    : page === 'methoden' ? PreviewMethoden
    : page === 'ueberMich' ? PreviewUeberMich
    : page === 'impressum' ? PreviewImpressum
    : page === 'datenschutz' ? PreviewDatenschutz
    : page === 'agb' ? PreviewAgb
    : null;

  if (!ContentRenderer) return null;

  const headerComp = components.header;
  const footerComp = components.footer;
  const cookieComp = components.cookie;

  return (
    <div className={rootClasses} style={rootStyle as any}>
      {settings.fxGrain && <div className="fx-grain-overlay" aria-hidden="true" />}
      {headerComp?.enabled && (
        <PreviewHeader data={headerComp.data} settings={settings} onToggleDarkMode={onToggleDarkMode} />
      )}
      <main id="main">
        <ContentRenderer settings={settings} components={components} />
      </main>
      {footerComp?.enabled && (
        <PreviewFooter data={footerComp.data} settings={settings} />
      )}
      {cookieComp?.enabled && (
        <PreviewCookie data={cookieComp.data} settings={settings} />
      )}
    </div>
  );
}
